import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Facebook, ArrowUp } from 'lucide-react';

export const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { href: '#home', label: 'Home' },
    { href: '#about', label: 'About Us' },
    { href: '#services', label: 'Services' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#reviews', label: 'Reviews' },
    { href: '#contact', label: 'Contact' },
  ];

  const services = [
    'Crack Filling',
    'Patchwork',
    'Asphalt Sealing',
    'Line Striping',
    'Color Coating',
    'Free Estimates'
  ];

  const serviceAreas = [
    'Central Massachusetts',
    'Metro West Boston',
    'Worcester County',
    'Middlesex County',
    'Norfolk County',
    'Greater Boston Area'
  ];

  return (
    <footer className="bg-black relative" role="contentinfo">
      {/* Red stripe top */}
      <div className="h-2 bg-red-600" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand column with BIG LOGO */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/images/logo.jpg"
                alt="Rhino Seal Asphalt Maintenance - Professional sealcoating company in Worcester MA"
                className="h-24 w-24 rounded-full object-cover border-4 border-red-600 shadow-lg shadow-red-600/30"
                width="96"
                height="96"
                loading="lazy"
              />
            </div>
            <div className="mb-4">
              <span className="block text-2xl font-black text-white uppercase tracking-tight">
                Rhino Seal
              </span>
              <span className="block text-sm text-red-500 uppercase tracking-wider font-bold">
                Asphalt Maintenance
              </span>
            </div>
            <p className="text-neutral-400 mb-6 leading-relaxed">
              The only place to call for all your pavement maintenance needs. 
              Serving Worcester County with professional sealcoating since 2005.
            </p>
            
            {/* Social stats */}
            <motion.a
              href="https://www.facebook.com/sealcoatingcentralmass/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-sm transition-colors"
              whileHover={{ scale: 1.05 }}
              aria-label="Follow Rhino Seal on Facebook - 1K Followers"
            >
              <Facebook className="w-4 h-4" aria-hidden="true" />
              <span className="font-semibold">1K Followers</span>
            </motion.a>
          </div>

          {/* Quick Links */}
          <nav aria-label="Footer navigation">
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-red-600 rounded-full" />
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <motion.a
                    href={link.href}
                    className="text-neutral-400 hover:text-red-500 transition-colors"
                    whileHover={{ x: 5 }}
                  >
                    {link.label}
                  </motion.a>
                </li>
              ))}
            </ul>
          </div>
          </nav>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-red-600 rounded-full" aria-hidden="true" />
              Our Services
            </h4>
            <ul className="space-y-3" aria-label="Our services list">
              {services.map((service) => (
                <li key={service} className="text-neutral-400">
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas & Contact */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-red-600 rounded-full" />
              Service Areas
            </h4>
            <ul className="space-y-2 mb-6">
              {serviceAreas.map((area) => (
                <li key={area} className="text-neutral-400 text-sm">
                  {area}
                </li>
              ))}
            </ul>

            <div className="space-y-3">
              <motion.a
                href="tel:7746967152"
                className="flex items-center gap-2 text-white hover:text-red-500 transition-colors"
                whileHover={{ x: 5, scale: 1.02 }}
                aria-label="Call Rhino Seal at (774) 696-7152"
              >
                <Phone className="w-4 h-4 text-red-500" aria-hidden="true" />
                (774) 696-7152
              </motion.a>
              <motion.a
                href="mailto:rhinoseal@charter.net"
                className="flex items-center gap-2 text-white hover:text-red-500 transition-colors"
                whileHover={{ x: 5, scale: 1.02 }}
                aria-label="Email Rhino Seal at rhinoseal@charter.net"
              >
                <Mail className="w-4 h-4 text-red-500" aria-hidden="true" />
                rhinoseal@charter.net
              </motion.a>
              <address className="flex items-center gap-2 text-neutral-400 not-italic">
                <MapPin className="w-4 h-4 text-red-500" aria-hidden="true" />
                Worcester, MA 01606
              </address>
            </div>
          </div>
        </div>

        {/* Bottom bar with Rhino */}
        <div className="mt-12 pt-8 border-t border-red-600/30 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/images/logo.jpg" alt="Rhino Seal Logo" className="w-8 h-8 rounded-full border border-red-600" loading="lazy" />
            <p className="text-neutral-500 text-sm">
              © {new Date().getFullYear()} Rhino Seal Asphalt Maintenance. All rights reserved.
            </p>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-neutral-500">
            <span>Sealcoating Worcester MA</span>
            <span className="hidden sm:inline text-red-600">|</span>
            <span>Crack Filling Central MA</span>
          </div>
        </div>
      </div>

      {/* Scroll to top button - RED */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-4 right-4 z-50 bg-red-600 text-white p-3 rounded-sm shadow-lg"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.1, boxShadow: '0 0 20px rgba(220, 38, 38, 0.6)' }}
        whileTap={{ scale: 0.9 }}
        data-testid="scroll-to-top"
        aria-label="Scroll to top of page"
      >
        <ArrowUp className="w-6 h-6" aria-hidden="true" />
      </motion.button>
    </footer>
  );
};

export default Footer;
